import { useEffect, useState } from 'react';
import { resolveTenant, resolveTenantFromPath } from './lib/tenant';
import { loadTenantByHostname, loadTenantBySlug } from './lib/tenants';
import { loadTenantFeatures } from './lib/featuresService';
import { isEntitled, gateFeature } from './lib/entitlements';
import type { TenantFeatures } from './lib/superAdmin';
import { themeFromTenant, applyTheme } from './lib/theming';
import { applyTenantTabIdentity } from './lib/favicon';
import type { Tenant } from './lib/database.types';
import MenuView from './components/MenuView';
import IntroSplash from './components/IntroSplash';
import MaintenancePage from './components/MaintenancePage';
import { isSiteActive, siteStatusOf } from './lib/siteStatus';
import OrderList from './components/OrderList';
import TabletGate from './components/TabletGate';
import Confirmation from './components/Confirmation';
import AdminGuard from './components/admin/AdminGuard';
import SuperAdminConsole from './components/admin/SuperAdminConsole';
import Signup from './components/Signup';
import Login from './components/Login';
import NetworkBanner from './components/NetworkBanner';

/**
 * App racine (Sprint 2-4) : multi-tenant data-driven + routage par chemin.
 *   /            → vue client (menu, panier, checkout)
 *   /tablet      → écran tablette (commandes temps réel)
 *   /admin       → gestion du menu
 *   /admin/super → console SuperAdmin
 */
type Route = 'client' | 'tablet' | 'admin' | 'super' | 'signup' | 'login' | 'merci';

function routeFromPath(path: string): Route {
  const p = path.toLowerCase();
  if (p.startsWith('/admin/super')) return 'super';
  if (p.startsWith('/admin')) return 'admin';
  if (p.startsWith('/tablet')) return 'tablet';
  if (p.startsWith('/merci') || p.startsWith('/confirmation')) return 'merci';
  if (p.startsWith('/signup') || p.startsWith('/inscription')) return 'signup';
  if (p.startsWith('/login') || p.startsWith('/connexion')) return 'login';
  return 'client';
}

export default function App() {
  // Résolution du tenant : 1) par le chemin /r/<slug> (self-service, sans DNS),
  // 2) sinon par le hostname (sous-domaine / domaine personnalisé — rétro-compatible).
  const pathInfo = resolveTenantFromPath(window.location.pathname);
  const locator = resolveTenant(window.location.hostname);
  const route = routeFromPath(pathInfo.rest);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  // Entitlements du resto (table tenant_features) — chargés une fois, passés aux vues.
  const [features, setFeatures] = useState<TenantFeatures>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = pathInfo.slug
        ? await loadTenantBySlug(pathInfo.slug)
        : await loadTenantByHostname(window.location.hostname);
      if (cancelled) return;
      setTenant(t);
      applyTheme(themeFromTenant(t));
      // Icône d'onglet = logo du resto (repli Lil & Co) + titre = nom du resto.
      if (t) applyTenantTabIdentity(t.name, t.logo_url);
      // Charge les entitlements après le tenant (résilient : {} si table absente/erreur).
      if (t) {
        const f = await loadTenantFeatures(t.id);
        if (!cancelled) setFeatures(f);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La console SuperAdmin n'est pas liée à un tenant unique.
  if (route === 'super') {
    return (
      <main className="min-h-screen">
        <NetworkBanner />
        <SuperAdminConsole />
      </main>
    );
  }

  // L'inscription libre-service n'est pas liée à un tenant (Brique 3).
  if (route === 'signup') {
    return (
      <main className="min-h-screen bg-brand-light text-brand-dark flex justify-center p-6">
        <Signup />
      </main>
    );
  }

  // Connexion (brique H) — pour se connecter en super_admin avant d'ouvrir la console.
  if (route === 'login') {
    return (
      <main className="min-h-screen bg-brand-light text-brand-dark flex justify-center p-6">
        <Login />
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-brand-light flex items-center justify-center">
        <p className="opacity-70">Chargement…</p>
      </main>
    );
  }

  if (!tenant) {
    return (
      <main className="min-h-screen bg-brand-light text-brand-dark flex flex-col items-center justify-center gap-3 p-8 text-center">
        <h1 className="text-2xl font-bold text-brand-primary">Lil &amp; Co — Click &amp; Collect</h1>
        <p className="max-w-md">
          Aucun restaurant pour <strong>{pathInfo.slug ?? locator.hostKey}</strong>
          {!pathInfo.slug && locator.country ? ` (${locator.country.toUpperCase()})` : ''}. Socle multi-tenant prêt.
        </p>
      </main>
    );
  }

  // Site suspendu / désactivé (cf. sql/31_site_status.sql) : page maintenance sur les
  // pages PUBLIQUES (site client + retour paiement). L'admin et la tablette restent
  // accessibles pour la gestion et la réactivation.
  if (!isSiteActive(tenant) && (route === 'client' || route === 'merci')) {
    return <MaintenancePage tenant={tenant} status={siteStatusOf(tenant)} />;
  }

  // Page de confirmation / retour de paiement (parité C6, legacy `/merci`).
  if (route === 'merci') {
    return (
      <main className="min-h-screen">
        <NetworkBanner />
        <Confirmation tenant={tenant} paid currency={tenant.currency} reviewsEnabled={isEntitled(features, 'reviews')} />
      </main>
    );
  }

  // Vue client : pleine largeur (header + hero gérés dans MenuView).
  if (route === 'client') {
    return (
      <main className="min-h-screen">
        <IntroSplash tenant={tenant} />
        <NetworkBanner />
        <MenuView tenant={tenant} features={features} />
      </main>
    );
  }

  // Back-office client : coque pleine largeur (sidebar + sections), parité legacy.
  if (route === 'admin') {
    return (
      <main className="min-h-screen">
        <NetworkBanner />
        <AdminGuard tenant={tenant} features={features} />
      </main>
    );
  }

  // Mode tablette / caisse : plein écran (la coque est gérée dans OrderList).
  return (
    <main className="min-h-screen">
      <TabletGate tenantId={tenant.id} pin={(tenant as { tablet_pin?: string | null }).tablet_pin}>
        <OrderList tenantId={tenant.id} currency={tenant.currency} tenantName={tenant.name} tenantSlug={tenant.slug} loyaltyEnabled={gateFeature(features, 'loyalty', tenant.loyalty_enabled)} />
      </TabletGate>
    </main>
  );
}
